import { useEffect, useRef, useState } from "react";
import { Activity, ACTIVITY_TYPES, hasRoute, isJourney, VEHICLE_GLYPH, coversDay, byTime, addDaysISO, parseISO } from "./plannerTypes";

// MapLibre GL is loaded from a CDN at runtime rather than bundled: this project
// is on react-scripts 4 (webpack 4), whose babel/parser chokes on the modern
// syntax in MapLibre's dist. Loading the prebuilt UMD at runtime sidesteps that
// and keeps it out of the main bundle. `maplibregl` is the global it exposes.
const MAPLIBRE_VER = "4.7.1";
const MAPLIBRE_JS = `https://unpkg.com/maplibre-gl@${MAPLIBRE_VER}/dist/maplibre-gl.js`;
const MAPLIBRE_CSS = `https://unpkg.com/maplibre-gl@${MAPLIBRE_VER}/dist/maplibre-gl.css`;

let maplibrePromise: Promise<any> | null = null;
const loadMapLibre = (): Promise<any> => {
  const w = window as any;
  if (w.maplibregl) return Promise.resolve(w.maplibregl);
  if (maplibrePromise) return maplibrePromise;
  maplibrePromise = new Promise((resolve, reject) => {
    if (!document.getElementById("maplibre-css")) {
      const link = document.createElement("link");
      link.id = "maplibre-css";
      link.rel = "stylesheet";
      link.href = MAPLIBRE_CSS;
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = MAPLIBRE_JS;
    script.async = true;
    script.onload = () => resolve((window as any).maplibregl);
    script.onerror = () => reject(new Error("Kunde inte ladda kartan"));
    document.head.appendChild(script);
  });
  return maplibrePromise;
};

const escapeHtml = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));

// Day-badge positions along their line — varied per badge so several lines from
// the same hotel don't stack their numbers at the same midpoint.
const BADGE_FRACTIONS = [0.5, 0.62, 0.4, 0.7, 0.34, 0.56, 0.46, 0.66];

// A basemap is either a hosted MapLibre vector style (OpenStreetMap via the
// keyless OpenFreeMap project) or a MapLibre style built around raster tiles.
type StyleSpec = string | Record<string, any>;
interface Basemap { label: string; icon: string; style: StyleSpec; }

const ofm = (name: string) => `https://tiles.openfreemap.org/styles/${name}`;
const rasterStyle = (tiles: string[], attribution: string, maxzoom = 19): Record<string, any> => ({
  version: 8,
  sources: { base: { type: "raster", tiles, tileSize: 256, attribution, maxzoom } },
  layers: [{ id: "base", type: "raster", source: "base" }],
});

const BASEMAPS: Record<string, Basemap> = {
  osm: { label: "Karta", icon: "🗺️", style: ofm("liberty") },
  light: { label: "Ljus", icon: "☀️", style: ofm("positron") },
  dark: {
    label: "Mörk", icon: "🌙",
    // Esri Dark Gray Canvas — keyless (CARTO now requires an API key).
    style: rasterStyle(
      ["https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"],
      "Tiles &copy; Esri",
      16
    ),
  },
  topo: {
    label: "Topografisk", icon: "⛰️",
    style: rasterStyle(
      ["a", "b", "c"].map((s) => `https://${s}.tile.opentopomap.org/{z}/{x}/{y}.png`),
      '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
      17
    ),
  },
  satellite: {
    label: "Satellit", icon: "🛰️",
    style: rasterStyle(
      ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"],
      "Imagery &copy; Esri",
      19
    ),
  },
};

interface Lodging { label: string; lat: number; lng: number; }
interface DayBase { day: number; lat: number; lng: number; } // effective accommodation per day

type LatLng = [number, number]; // [lat, lng] internally; converted to [lng, lat] at the MapLibre edge
const lngLat = (p: LatLng): [number, number] => [p[1], p[0]];

// Parametric path of a leg: progress u∈[0,1] → point. Flights bow out along a
// perpendicular arc; everything else is a straight line. The drawn flight line
// and the playback plane use the SAME function so the plane rides the line.
const legPath = (from: LatLng, to: LatLng, arc: boolean) => {
  const dLat = to[0] - from[0];
  const dLng = to[1] - from[1];
  const len = Math.hypot(dLat, dLng) || 1;
  const perpLat = -dLng / len;
  const perpLng = dLat / len;
  const amp = arc ? len * 0.18 : 0;
  return (u: number): LatLng => {
    const bulge = amp * Math.sin(Math.PI * u);
    return [from[0] + dLat * u + perpLat * bulge, from[1] + dLng * u + perpLng * bulge];
  };
};
// Sample a leg into a [lng,lat] coordinate list for a GeoJSON LineString.
const legCoords = (from: LatLng, to: LatLng, arc: boolean, steps = 48): [number, number][] => {
  if (!arc) return [lngLat(from), lngLat(to)];
  const path = legPath(from, to, arc);
  const out: [number, number][] = [];
  for (let i = 0; i <= steps; i++) out.push(lngLat(path(i / steps)));
  return out;
};

// Map of all activities that have geocoded coordinates, with colored pins.
// Accommodation bases (trip base + per-day overrides) get 🏨 pins, with a
// numbered dotted line from each day's base to that day's activities.
export const TripMap = ({
  activities,
  lodgings = [],
  bases = [],
  startISO = "",
  dayCount = 0,
}: {
  activities: Activity[];
  lodgings?: Lodging[];
  bases?: DayBase[];
  startISO?: string; // trip start date — dates the playback banner
  dayCount?: number; // number of days — drives the playback timeline
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const mlRef = useRef<any>(null); // the maplibregl namespace
  const markersRef = useRef<any[]>([]);
  const renderRef = useRef<() => void>(() => {});
  const fitRef = useRef<() => void>(() => {}); // re-fit to all points on demand
  const cancelRef = useRef(false); // playback stop flag
  const flyMarkerRef = useRef<any>(null); // the moving plane/car marker
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [playInfo, setPlayInfo] = useState<{
    day: string;
    date: string;
    acts: { id: string; icon: string; title: string; time: string }[];
    activeId: string | null;
  } | null>(null);
  const [basemap, setBasemap] = useState<string>(
    () => localStorage.getItem("planner-basemap") || "osm"
  );
  const changeBasemap = (id: string) => { setBasemap(id); localStorage.setItem("planner-basemap", id); };

  // Anything mappable: has a start point, OR (routed) an end point — so a drive
  // with only an end (or a hotel-anchored journey) still shows.
  const drawable = activities.filter(
    (a) =>
      (a.lat !== null && a.lng !== null) ||
      (hasRoute(a.type) && a.endLat !== null && a.endLng !== null)
  );

  // A custom-HTML marker (reuses the .planner-pin / .planner-daynum styling).
  const addMarker = (
    p: LatLng,
    className: string,
    inner: string,
    popupHtml?: string,
    interactive = true
  ) => {
    const map = mapRef.current;
    const maplibregl = mlRef.current;
    const el = document.createElement("div");
    el.className = className;
    el.innerHTML = inner;
    if (!interactive) el.style.pointerEvents = "none";
    const marker = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(lngLat(p));
    if (popupHtml) {
      marker.setPopup(new maplibregl.Popup({ offset: 16, closeButton: false }).setHTML(popupHtml));
    }
    marker.addTo(map);
    markersRef.current.push(marker);
  };

  const pinHtml = (color: string, glyph: string) => `<span style="background:${color}">${glyph}</span>`;

  // Add (or refresh) the line source + layers. Two dash styles → two layers:
  // yellow "connector" lines (hotel→activity / journeys) and colored activity
  // "route" lines (e.g. the hike path). line-dasharray can't be data-driven, so
  // they're split; line-color on the route layer is read per feature.
  // Lines live in the style, so they can only be added once the style is loaded.
  // Markers/fit don't need the style — see render(). Defer if not ready yet.
  const applyLines = (features: any[]) => {
    const map = mapRef.current;
    if (!map) return;
    if (map.isStyleLoaded()) setLines(features);
    else map.once("idle", () => setLines(features));
  };
  const setLines = (features: any[]) => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const fc = { type: "FeatureCollection", features };
    const src = map.getSource("trip-lines");
    if (src) { src.setData(fc); return; }
    map.addSource("trip-lines", { type: "geojson", data: fc });
    map.addLayer({
      id: "trip-lines-connector",
      type: "line",
      source: "trip-lines",
      filter: ["==", ["get", "kind"], "connector"],
      layout: { "line-cap": "round" },
      paint: { "line-color": "#e2b31a", "line-width": 2, "line-dasharray": [1, 2.5], "line-opacity": 0.75 },
    });
    map.addLayer({
      id: "trip-lines-route",
      type: "line",
      source: "trip-lines",
      filter: ["==", ["get", "kind"], "route"],
      layout: { "line-cap": "round" },
      paint: { "line-color": ["get", "color"], "line-width": 3, "line-dasharray": [1.5, 2], "line-opacity": 0.9 },
    });
  };

  const lineFeature = (
    from: LatLng,
    to: LatLng,
    kind: "connector" | "route",
    color?: string,
    arc = false
  ) => ({
    type: "Feature",
    properties: { kind, ...(color ? { color } : {}) },
    geometry: { type: "LineString", coordinates: legCoords(from, to, arc) },
  });

  // Connector line through an ordered list of waypoints (start, ...stops, end),
  // arcing each segment for flights. Used when a journey has layovers/stops.
  const journeyLineFeature = (wps: LatLng[], arc: boolean) => {
    const coords: [number, number][] = [];
    for (let i = 0; i < wps.length - 1; i++) {
      const seg = legCoords(wps[i], wps[i + 1], arc);
      if (i > 0) seg.shift(); // drop the duplicated shared point
      coords.push(...seg);
    }
    return { type: "Feature", properties: { kind: "connector" }, geometry: { type: "LineString", coordinates: coords } };
  };

  // Rebuild all markers + lines from the current props, then fit the view.
  // Markers and fit only need the map instance (not the loaded style/tiles), so
  // the trip shows even while the basemap is still streaming in.
  const render = () => {
    const map = mapRef.current;
    if (!map || !mlRef.current) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    if (drawable.length === 0 && lodgings.length === 0) {
      applyLines([]);
      return;
    }

    const lines: any[] = [];
    const latlngs: LatLng[] = []; // everything (fallback)
    const fitPts: LatLng[] = []; // excludes flight endpoints so "Visa alla" frames the trip
    let badgeSeq = 0; // cycles BADGE_FRACTIONS so badges spread along their lines

    const baseForDay = new Map<number, LatLng>();
    bases.forEach((b) => baseForDay.set(b.day, [b.lat, b.lng]));

    const lodgePts: LatLng[] = [];
    lodgings.forEach((l) => {
      addMarker(
        [l.lat, l.lng],
        "planner-pin",
        pinHtml("#c0566b", "🏨"),
        `<b>🏨 Boende</b>${l.label ? `<br>${escapeHtml(l.label)}` : ""}`
      );
      latlngs.push([l.lat, l.lng]);
      fitPts.push([l.lat, l.lng]);
      lodgePts.push([l.lat, l.lng]);
    });

    drawable.forEach((a) => {
      const meta = ACTIVITY_TYPES[a.type];
      const dayTxt = a.day === null ? "Idéer" : `Dag ${a.day + 1}`;
      const isJourneyLeg = isJourney(a.type);
      const start: LatLng | null = a.lat != null && a.lng != null ? [a.lat, a.lng] : null;
      const end: LatLng | null =
        hasRoute(a.type) && a.endLat != null && a.endLng != null ? [a.endLat, a.endLng] : null;
      const origin: LatLng | null =
        a.travelFromOther && a.originLat != null && a.originLng != null ? [a.originLat, a.originLng] : null;
      const hotel = a.day !== null ? baseForDay.get(a.day) : undefined;

      // pins: start (activity/departure), end (🏁), custom origin (🚩)
      if (start) {
        addMarker(
          start,
          "planner-pin",
          pinHtml(meta.color, meta.icon),
          `<b>${escapeHtml(a.title)}</b><br>${dayTxt}` +
            (a.flightNumber ? `<br>✈️ ${escapeHtml(a.flightNumber)}` : "") +
            (a.location ? `<br>${end ? "Start: " : ""}${escapeHtml(a.location)}` : "")
        );
        latlngs.push(start);
        if (a.type !== "flight") fitPts.push(start);
      }
      if (end) {
        addMarker(
          end,
          "planner-pin",
          pinHtml(meta.color, "🏁"),
          `<b>${escapeHtml(a.title)}</b><br>${dayTxt}` +
            (a.endLocation ? `<br>Mål: ${escapeHtml(a.endLocation)}` : "")
        );
        latlngs.push(end);
        if (a.type !== "flight") fitPts.push(end);
      }
      if (origin) {
        addMarker(
          origin,
          "planner-pin",
          pinHtml("#888", "🚩"),
          `<b>🚩 Start</b>${a.originLocation ? `<br>${escapeHtml(a.originLocation)}` : ""}`
        );
        latlngs.push(origin);
        fitPts.push(origin);
      }

      const badgeAt = (p1: LatLng, p2: LatLng) => {
        if (a.day === null) return;
        const f = BADGE_FRACTIONS[badgeSeq++ % BADGE_FRACTIONS.length];
        // sit the badge on the drawn line — arced for flights, straight otherwise
        addMarker(
          legPath(p1, p2, a.type === "flight")(f),
          "planner-daynum",
          `<span>${a.day + 1}</span>`,
          undefined,
          false
        );
      };

      if (isJourneyLeg) {
        // the drive/flight/train/boat itself is the day's travel; fall back to
        // the hotel for whichever endpoint has no coordinates.
        const jFrom = origin ?? start ?? hotel;
        const jTo = end ?? hotel;
        if (jFrom && jTo && (jFrom[0] !== jTo[0] || jFrom[1] !== jTo[1])) {
          const stopPts = (a.stops ?? [])
            .filter((s) => s.lat != null && s.lng != null)
            .map((s) => [s.lat as number, s.lng as number] as LatLng);
          const wps = [jFrom, ...stopPts, jTo];
          lines.push(journeyLineFeature(wps, a.type === "flight"));
          // pins at each layover/stop
          (a.stops ?? []).forEach((s) => {
            if (s.lat == null || s.lng == null) return;
            addMarker(
              [s.lat, s.lng],
              "planner-pin planner-pin--stop",
              pinHtml(meta.color, "•"),
              `<b>🛑 Stopp</b>${s.location ? `<br>${escapeHtml(s.location)}` : ""}`
            );
            latlngs.push([s.lat, s.lng]);
            if (a.type !== "flight") fitPts.push([s.lat, s.lng]);
          });
          badgeAt(wps[0], wps[1]);
        }
      } else {
        // non-journey: "how you get there" line (origin/hotel → activity)
        const from = origin ?? hotel;
        if (from && start) {
          lines.push(lineFeature(from, start, "connector"));
          badgeAt(from, start);
        }
        // the activity's own route (e.g. the hike path)
        if (start && end) {
          lines.push(lineFeature(start, end, "route", meta.color));
        }
      }
    });

    applyLines(lines);

    const fit = () => {
      // Frame the trip itself. Start from non-flight points; if accommodation is
      // set, also drop anything >250km from it (far flights/drives) so the view
      // stays tight on where you actually are.
      const km = (a: LatLng, b: LatLng) => {
        const R = 6371;
        const dLat = ((b[0] - a[0]) * Math.PI) / 180;
        const dLng = ((b[1] - a[1]) * Math.PI) / 180;
        const s =
          Math.sin(dLat / 2) ** 2 +
          Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
      };
      let pts = fitPts;
      if (lodgePts.length > 0) {
        const near = fitPts.filter((p) => lodgePts.some((l) => km(p, l) <= 250));
        pts = near.length > 0 ? near : lodgePts;
      }
      if (pts.length === 0) pts = latlngs;
      if (pts.length === 1) map.easeTo({ center: lngLat(pts[0]), zoom: 12 });
      else if (pts.length > 1) {
        const b = new mlRef.current.LngLatBounds();
        pts.forEach((p) => b.extend(lngLat(p)));
        map.fitBounds(b, { padding: 44, maxZoom: 14, duration: 0 });
      }
    };
    fitRef.current = fit;
    fit();
  };
  renderRef.current = render;

  // init once (after the CDN library resolves)
  useEffect(() => {
    let cancelled = false;
    loadMapLibre()
      .then((maplibregl) => {
        if (cancelled || !containerRef.current || mapRef.current) return;
        mlRef.current = maplibregl;
        const map = new maplibregl.Map({
          container: containerRef.current,
          style: (BASEMAPS[basemap] ?? BASEMAPS.osm).style,
          center: [5, 25],
          zoom: 1.4,
          attributionControl: { compact: true },
        });
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
        map.dragRotate.disable();
        map.touchZoomRotate.disableRotation();
        mapRef.current = map;
        // Ready as soon as the map exists — markers/fit don't wait for tiles, so
        // the trip shows (and stays interactive) even if the basemap is slow.
        setReady(true);
        renderRef.current();
        map.on("load", () => {
          map.resize();
          renderRef.current();
        });
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      const map = mapRef.current;
      if (map) map.remove();
      mapRef.current = null;
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // switch basemap: setStyle drops sources/layers, so re-render once it's back
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    map.setStyle((BASEMAPS[basemap] ?? BASEMAPS.osm).style);
    // setStyle drops our source/layers; re-add once the new style has settled.
    map.once("idle", () => renderRef.current());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basemap]);

  // redraw markers/lines whenever the located activities or lodgings change
  const signature =
    drawable
      .map(
        (p) =>
          `${p.id}:${p.lat},${p.lng}:${p.endLat},${p.endLng}:${p.day}:${p.type}:${p.title}:${p.travelFromOther ? p.originLat + "," + p.originLng : ""}:${(p.stops ?? []).map((s) => `${s.lat},${s.lng}`).join(">")}`
      )
      .join("|") +
    "#" +
    lodgings.map((l) => `${l.lat},${l.lng}:${l.label}`).join("|") +
    "@" +
    bases.map((b) => `${b.day}:${b.lat},${b.lng}`).join("|");
  useEffect(() => {
    if (ready) renderRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, ready]);

  // keep isFull in sync with the Fullscreen API and re-measure the map on change
  useEffect(() => {
    const onChange = () => {
      const el = document.fullscreenElement || (document as any).webkitFullscreenElement;
      setIsFull(!!el);
      setTimeout(() => mapRef.current?.resize(), 60);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  const toggleFull = () => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const fsEl = document.fullscreenElement || (document as any).webkitFullscreenElement;
    if (fsEl) {
      (document.exitFullscreen || (document as any).webkitExitFullscreen)?.call(document);
    } else {
      (wrap.requestFullscreen || (wrap as any).webkitRequestFullscreen)?.call(wrap);
    }
  };

  // ---- trip playback: fly/drive the route day by day ----
  const wait = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));
  const fmtDate = (iso: string) => {
    const d = parseISO(iso);
    return d ? d.toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "short" }) : "";
  };

  // pan/zoom the camera and resolve when the move is done
  const cameraP = (fn: (map: any, ml: any) => void, ms: number) =>
    new Promise<void>((res) => {
      const map = mapRef.current;
      if (!map || !mlRef.current) return res();
      fn(map, mlRef.current);
      setTimeout(res, ms + 60);
    });

  // move a plane/car marker along a leg (flights arc; drives go straight)
  const animateLeg = (from: LatLng, to: LatLng, glyph: string, arc: boolean, ms: number) =>
    new Promise<void>((res) => {
      const map = mapRef.current;
      const ml = mlRef.current;
      if (!map || !ml) return res();
      const el = document.createElement("div");
      el.className = "planner-fly";
      el.textContent = glyph;
      const marker = new ml.Marker({ element: el, anchor: "center" }).setLngLat(lngLat(from)).addTo(map);
      flyMarkerRef.current = marker;
      const path = legPath(from, to, arc); // same curve the line was drawn from
      const t0 = performance.now();
      const step = (now: number) => {
        if (cancelRef.current) { marker.remove(); flyMarkerRef.current = null; return res(); }
        const t = Math.min(1, (now - t0) / ms);
        const te = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; // ease in-out
        marker.setLngLat(lngLat(path(te))); // ride the drawn arc exactly
        if (t < 1) requestAnimationFrame(step);
        else { marker.remove(); flyMarkerRef.current = null; res(); }
      };
      requestAnimationFrame(step);
    });

  const endPlay = (refit: boolean) => {
    cancelRef.current = true;
    if (flyMarkerRef.current) { flyMarkerRef.current.remove(); flyMarkerRef.current = null; }
    setPlaying(false);
    setPlayInfo(null);
    if (refit) fitRef.current();
  };

  const runPlayback = async () => {
    const baseForDay = new Map<number, LatLng>();
    bases.forEach((b) => baseForDay.set(b.day, [b.lat, b.lng]));

    for (let d = 0; d < dayCount; d++) {
      if (cancelRef.current) return;
      const date = fmtDate(addDaysISO(startISO, d));
      // Full day agenda for the panel (incl. non-mapped items like Mat/Vila, and
      // multi-day activities on every day they span, not just their start day)…
      const dayActs = activities.filter((a) => coversDay(a, d)).sort(byTime);
      // …but only animate mappable activities on their START day (so a 2-day
      // flight flies once, and a beach/hotel stay doesn't re-zoom every day).
      // Hotels are shown in the agenda only, never animated.
      const acts = drawable.filter((a) => a.day === d && a.type !== "hotel").sort(byTime);
      setPlayInfo({
        day: `Dag ${d + 1}`,
        date,
        acts: dayActs.map((a) => ({
          id: a.id,
          icon: ACTIVITY_TYPES[a.type].icon,
          title: a.title || ACTIVITY_TYPES[a.type].label,
          time: a.time || "",
        })),
        activeId: null,
      });
      if (acts.length === 0) {
        // nothing to animate: linger so the day's agenda can be read
        await wait(dayActs.length > 0 ? 1600 : 600);
        continue;
      }
      for (const a of acts) {
        if (cancelRef.current) return;
        setPlayInfo((pi) => (pi ? { ...pi, activeId: a.id } : pi));
        const start: LatLng | null = a.lat != null && a.lng != null ? [a.lat, a.lng] : null;
        const end: LatLng | null =
          hasRoute(a.type) && a.endLat != null && a.endLng != null ? [a.endLat, a.endLng] : null;
        const origin: LatLng | null =
          a.travelFromOther && a.originLat != null && a.originLng != null ? [a.originLat, a.originLng] : null;
        const hotel = baseForDay.get(d);

        if (isJourney(a.type)) {
          const from = origin ?? start ?? hotel;
          const to = end ?? hotel;
          if (from && to && (from[0] !== to[0] || from[1] !== to[1])) {
            const stopPts = (a.stops ?? [])
              .filter((s) => s.lat != null && s.lng != null)
              .map((s) => [s.lat as number, s.lng as number] as LatLng);
            const wps = [from, ...stopPts, to];
            await cameraP((map, ml) => {
              const b = new ml.LngLatBounds();
              wps.forEach((p) => b.extend(lngLat(p)));
              map.fitBounds(b, { padding: 90, duration: 900, maxZoom: 9 });
            }, 900);
            if (cancelRef.current) return;
            const glyph = VEHICLE_GLYPH[a.type] || "🚗";
            const isFlight = a.type === "flight";
            // fly/drive each segment in turn (through the layovers)
            for (let i = 0; i < wps.length - 1; i++) {
              if (cancelRef.current) return;
              await animateLeg(wps[i], wps[i + 1], glyph, isFlight, isFlight ? 3200 : 2600);
              if (i < wps.length - 2) await wait(400); // pause at each stop
            }
            await wait(300);
            continue;
          }
        }
        const pt = start ?? end;
        if (pt) {
          await cameraP((map) => map.easeTo({ center: lngLat(pt), zoom: 11, duration: 1000 }), 1000);
          await wait(1100);
        }
      }
    }
    if (!cancelRef.current) endPlay(true);
  };

  const togglePlay = () => {
    if (playing) { endPlay(true); return; }
    if (!startISO || dayCount <= 0 || drawable.length === 0) return;
    cancelRef.current = false;
    setPlaying(true);
    void runPlayback();
  };

  // stop playback if the map unmounts
  useEffect(() => () => { cancelRef.current = true; }, []);

  const hasPoints = drawable.length > 0 || lodgings.length > 0;

  return (
    <div className="planner__map-wrap" ref={wrapRef}>
      <div className="planner__map" ref={containerRef} />
      {failed && (
        <p className="planner__day-hint">Kunde inte ladda kartan. Kontrollera din uppkoppling.</p>
      )}
      <div className="planner__map-styles">
        {Object.entries(BASEMAPS).map(([id, b]) => (
          <button
            key={id}
            type="button"
            className={`planner__map-style${basemap === id ? " planner__map-style--active" : ""}`}
            title={b.label}
            onClick={() => changeBasemap(id)}
          >
            {b.icon}
          </button>
        ))}
      </div>
      <div className="planner__map-tools">
        <button
          type="button"
          className="planner__map-btn"
          title={isFull ? "Avsluta helskärm" : "Helskärm"}
          onClick={toggleFull}
        >
          {isFull ? "⤡ Stäng" : "⛶ Helskärm"}
        </button>
        {hasPoints && (
          <button
            type="button"
            className="planner__map-btn"
            title="Anpassa kartan till resan"
            onClick={() => fitRef.current()}
          >
            ⤢ Fit
          </button>
        )}
      </div>
      {hasPoints && startISO && dayCount > 0 && (
        <button
          type="button"
          className={`planner__map-play${playing ? " planner__map-play--on" : ""}`}
          title={playing ? "Stoppa uppspelning" : "Spela upp resan"}
          onClick={togglePlay}
        >
          {playing ? "⏹ Stoppa" : "▶ Spela resan"}
        </button>
      )}
      {playInfo && (
        <div className="planner__play-panel">
          <div className="planner__play-head">
            <span className="planner__play-day">{playInfo.day}</span>
            <span className="planner__play-date">{playInfo.date}</span>
          </div>
          <ul className="planner__play-list">
            {playInfo.acts.length === 0 && (
              <li className="planner__play-item planner__play-item--muted">🗓️ Inget inplanerat</li>
            )}
            {playInfo.acts.map((a) => (
              <li
                key={a.id}
                className={`planner__play-item${a.id === playInfo.activeId ? " planner__play-item--active" : ""}`}
              >
                <span className="planner__play-item-icon">{a.icon}</span>
                <span className="planner__play-item-title">{a.title}</span>
                {a.time && <span className="planner__play-item-time">{a.time}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!hasPoints && (
        <p className="planner__day-hint">
          Planera in en aktivitet med vald plats (autocomplete) så dyker den upp här.
        </p>
      )}
    </div>
  );
};
